"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, CircleHelp } from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Avatar from "sigma-avatars";
import { AuthHeroPanel } from "@/components/auth/auth-hero-panel";
import { AuthLegalNotice } from "@/components/auth-legal-notice";
import { AuthNavbar } from "@/components/auth-navbar";
import { RecoveryPhraseSetup } from "@/components/recovery/recovery-phrase-setup";
import { useSeedCreation } from "@/hooks/use-seed-creation";
import type { SeedBackup } from "@/lib/bap/seed-contract";

// The overlay (and its framer-motion dependency) only renders after the user
// submits, so keep it out of the initial route chunk.
const DecryptedTextLoadingOverlay = dynamic(() => import("@/components/decrypted-text-loading-overlay").then(m => m.DecryptedTextLoadingOverlay), {
  ssr: false
});
import { type SyncStep, useSyncStatus } from "@/components/sync-status-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useBapProfile } from "@/hooks/use-bap-profile";
import { useOAuthClientInfo } from "@/hooks/use-login-queries";
import { authClient } from "@/lib/auth-client";
import { signInWithBackup } from "@/lib/auth-flows";
import { enableNewWalletCloudBackup, getPendingNewWalletCloudBackupBapId } from "@/lib/bap/cloud-refresh";
import { getBrandConfig } from "@/lib/brand-config";
import { useFileDrop } from "@/lib/hooks/use-file-drop";
import { getDisplayImageUrl } from "@/lib/image-utils";
import { logger } from "@/lib/logger";
import { downloadCurrentEncryptedBackup, generateAndEncryptWallet, isPreparedSeedWalletSaved, MIN_PASSWORD_LENGTH, persistPreparedSeedWallet, prepareSeedWallet, validateSignupForm } from "@/lib/onboarding-wallet";
import { STORAGE_KEYS } from "@/lib/storage-keys";
const MAC_PLATFORM_REGEX = /Mac|iPhone|iPad|iPod/;

/** Small "?" that keeps the how-it-works detail out of the page. */
function HelpTip(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TooltipTrigger asChild={true}><button aria-label="More about this" className="inline-flex translate-y-[2px] text-muted-foreground transition-colors hover:text-foreground" type="button"><CircleHelp aria-hidden={true} className="h-3.5 w-3.5" /></button></TooltipTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <TooltipProvider><Tooltip>{t1}<TooltipContent className="max-w-xs text-pretty">{children}</TooltipContent></Tooltip></TooltipProvider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/** Hook to manage Cmd/Ctrl+O shortcut and platform detection */
function useImportShortcut(elementId) {
  const $ = _c(6);
  const [isMac, setIsMac] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof navigator === "undefined") {
        return;
      }
      setIsMac(MAC_PLATFORM_REGEX.test(navigator.platform));
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== elementId || $[3] !== isMac) {
    t2 = () => {
      const handleKeyDown = event => {
        const openKey = event.key.toLowerCase() === "o";
        const modifier = isMac ? event.metaKey : event.ctrlKey;
        if (!(modifier && openKey)) {
          return;
        }
        event.preventDefault();
        document.getElementById(elementId)?.click();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t3 = [isMac, elementId];
    $[2] = elementId;
    $[3] = isMac;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return isMac;
}
function SignupSubmitButton(t0) {
  const $ = _c(9);
  const {
    loading,
    pendingCloudBackup,
    onRetry
  } = t0;
  if (pendingCloudBackup) {
    const t1 = loading ? "Retrying..." : "Retry cloud backup";
    let t2;
    if ($[0] !== loading || $[1] !== onRetry || $[2] !== t1) {
      t2 = <Button className="w-full min-w-0" disabled={loading} onClick={onRetry} type="button">{t1}</Button>;
      $[0] = loading;
      $[1] = onRetry;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const t1 = loading ? "Creating..." : "Create wallet";
  let t2;
  if ($[4] !== t1) {
    t2 = <span className="min-w-0 whitespace-normal text-center">{t1}</span>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== loading || $[7] !== t2) {
    t3 = <Button className="w-full min-w-0" disabled={loading} type="submit">{t2}</Button>;
    $[6] = loading;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const brandConfig = getBrandConfig();
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seedCreationEnabled = useSeedCreation();
  const [seedDraft, setSeedDraft] = useState<SeedBackup | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [pendingCloudBackupBapId, setPendingCloudBackupBapId] = useState<string | null>(null);

  // Cmd/Ctrl+O still opens the (hidden) backup import, which hands off to /login.
  useImportShortcut("signup-backup-import");

  // OAuth context (if user reached signup from an OAuth flow)
  const clientId = searchParams.get("client_id");
  const oauthClientQuery = useOAuthClientInfo(clientId || null);
  const oauthClient = oauthClientQuery.data || null;
  const clientProfileQuery = useBapProfile(clientId ? oauthClient?.ownerBapId || null : null);
  const clientProfile = clientProfileQuery.data || null;
  const loadingClientInfo = oauthClientQuery.isLoading || clientProfileQuery.isLoading;
  const clientImageUrl = useMemo(() => {
    const img = clientProfile?.identity?.image;
    if (!img) {
      return null;
    }
    return getDisplayImageUrl(img) || null;
  }, [clientProfile]);
  const loginHref = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/login?${query}` : "/login";
  }, [searchParams]);
  useEffect(() => {
    const pendingBapId = getPendingNewWalletCloudBackupBapId();
    if (!pendingBapId) {
      return;
    }
    let cancelled = false;
    const hydratePendingRetry = async () => {
      const session = await authClient.getSession({
        query: {
          disableCookieCache: true
        }
      });
      if (!session.data?.user) {
        return;
      }
      const response = await fetch("/api/user/bap-ids", {
        credentials: "include"
      });
      if (!response.ok) {
        return;
      }
      const data = (await response.json()) as {
        bapIds?: Array<{
          id?: string;
        }>;
      };
      if (!cancelled && data.bapIds?.some(profile => profile.id === pendingBapId)) {
        setPendingCloudBackupBapId(pendingBapId);
        setError("Cloud backup was not completed. Retry to finish safely.");
      }
    };
    hydratePendingRetry().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const handleImportBackupFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      sessionStorage.setItem(STORAGE_KEYS.PENDING_IMPORT_BACKUP, text);
    } catch {
      // Ignore - login page will show import errors if needed.
    }
    router.push(loginHref);
  }, [loginHref, router]);
  const handleImportBackupInput = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e.target.files?.[0];
    e.target.value = "";
    if (!file_0) {
      return;
    }
    await handleImportBackupFile(file_0);
  }, [handleImportBackupFile]);
  const {
    isFileDragActive
  } = useFileDrop({
    enabled: true,
    onFile: handleImportBackupFile
  });

  // Initialize sync status with predefined steps
  const initialSyncSteps: SyncStep[] = [{
    id: "decrypt",
    label: "Preparing wallet...",
    status: "pending"
  }, {
    id: "discover",
    label: "Registering identities...",
    status: "pending"
  }, {
    id: "register",
    label: "Syncing identities...",
    status: "pending"
  }, {
    id: "complete",
    label: "Finalizing...",
    status: "pending"
  }, {
    id: "redirect",
    label: "Redirecting...",
    status: "pending"
  }];
  const {
    steps: syncSteps,
    updateStep
  } = useSyncStatus(initialSyncSteps);
  const continueAfterWalletCreation = useCallback(() => {
    // The identity exists by now, so the file the user keeps is the one
    // that can sign in on another device (a file saved before enrollment
    // has no identity in it).
    downloadCurrentEncryptedBackup(displayName);
    if (clientId) {
      return;
    }
    const returnTo = searchParams.get("return_to");
    router.push(returnTo || "/onboarding");
  }, [clientId, displayName, router, searchParams]);
  const handleRetryCloudBackup = useCallback(async () => {
    if (!pendingCloudBackupBapId) {
      return;
    }
    setLoading(true);
    setError("");
    const result = await enableNewWalletCloudBackup(pendingCloudBackupBapId);
    if (result.status !== "flushed") {
      setError("Cloud backup still couldn't be enabled. Check your connection and retry.");
      setLoading(false);
      return;
    }
    setPendingCloudBackupBapId(null);
    setLoading(false);
    continueAfterWalletCreation();
  }, [continueAfterWalletCreation, pendingCloudBackupBapId]);
  const commitWallet = useCallback(async (confirmedSeed?: SeedBackup) => {
    setLoading(true);
    setError("");
    setPendingCloudBackupBapId(null);
    setShowSyncDialog(true);
    try {
      updateStep("decrypt", "in_progress");
      const {
        backup
      } = confirmedSeed ? await persistPreparedSeedWallet(confirmedSeed, password) : await generateAndEncryptWallet(displayName, password);
      updateStep("discover", "in_progress");
      const result_0 = await signInWithBackup(backup, {
        enrollNewProfile: true,
        onSyncProgress: (step, status, message) => {
          updateStep(step, status, message);
        }
      });
      if (result_0.errorCode === "CLOUD_BACKUP_ENABLE_FAILED") {
        setPendingCloudBackupBapId(getPendingNewWalletCloudBackupBapId());
        setShowSyncDialog(false);
        setError(result_0.error || "Cloud backup couldn't be enabled. Retry to finish safely.");
        updateStep("complete", "error", "Cloud backup not enabled");
        return;
      }
      if (!result_0.ok) {
        throw new Error(result_0.error || "Sign up failed");
      }
      updateStep("redirect", "in_progress");
      continueAfterWalletCreation();
    } catch (signupError) {
      logger.error({
        operation: "signup_create_wallet"
      }, "Signup failed");
      setError(signupError instanceof Error && signupError.message ? signupError.message : "Signup failed. Please try again.");
      updateStep("decrypt", "error", "Signup failed");
      sessionStorage.removeItem(STORAGE_KEYS.TEMP_PASSWORD);
      // Close the full-screen sync overlay so the error alert is actually
      // visible — leaving it open shows an endless spinner over the form.
      setShowSyncDialog(false);
    } finally {
      setLoading(false);
    }
  }, [continueAfterWalletCreation, displayName, password, updateStep]);
  const handleCreateWallet = useCallback(async (e_0?: React.FormEvent, confirmedSeed_0?: SeedBackup) => {
    e_0?.preventDefault();
    const validationError = validateSignupForm(displayName, password, confirmPassword);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (seedCreationEnabled && !confirmedSeed_0) {
      try {
        setSeedDraft(prepareSeedWallet(displayName));
        setError("");
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Could not prepare recovery words");
      }
      return;
    }
    await commitWallet(confirmedSeed_0);
  }, [displayName, password, confirmPassword, seedCreationEnabled, commitWallet]);
  return <div className="flex min-h-svh min-w-0 flex-col overflow-x-clip">
			{isFileDragActive ? <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-background/60 backdrop-blur-sm">
					<div className="rounded-xl border border-dashed bg-background/80 px-6 py-4 text-center shadow-lg">
						<div className="font-medium">Drop your backup to import</div>
						<div className="mt-1 text-muted-foreground text-xs">
							Accepts .bep and .json
						</div>
					</div>
				</div> : null}
			<AuthNavbar oauthClientName={oauthClient?.clientName} />

			<div className="grid min-w-0 flex-1 lg:grid-cols-2">
				{/* Left: form column (matches the shadcn reference layout structure) */}
				<div className="flex min-w-0 flex-col gap-4 p-4 sm:p-6 md:p-10">
					<div className="flex min-w-0 flex-1 items-center justify-center">
						<div className="w-full min-w-0 max-w-md">
							<Card className="min-w-0">
								<CardContent className="flex min-w-0 flex-col gap-6 p-4 sm:p-6 md:p-8">
									<div className="flex flex-col items-center gap-2 text-center">
										<div className="h-12 w-12 overflow-hidden rounded-full bg-muted ring-1 ring-border">
											<Avatar colors={["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"]} name={displayName.trim() || brandConfig.name} size={48} variant="pixel" />
										</div>
										<div>
											<h1 className="font-semibold text-xl">
												Create your wallet
											</h1>
											<p className="mt-1 text-balance text-muted-foreground text-sm">
												No email, phone number, or third-party login required.{" "}
												<HelpTip>
													You choose a password and download an encrypted
													backup. Your key is your account; everything else is
													optional.
												</HelpTip>
											</p>
										</div>
									</div>

									{loadingClientInfo ? <div className="rounded-lg border p-4">
											<div className="flex items-center gap-3">
												<Skeleton className="h-10 w-10 rounded-full" />
												<div className="min-w-0 flex-1 space-y-2">
													<Skeleton className="h-4 w-full max-w-40" />
													<Skeleton className="h-3 w-full max-w-56" />
												</div>
											</div>
										</div> : null}

									{error ? <Alert variant="destructive">
											<AlertTriangle className="h-4 w-4" />
											<AlertDescription>{error}</AlertDescription>
										</Alert> : null}

									{seedDraft && !pendingCloudBackupBapId ? <RecoveryPhraseSetup backupSaved={isPreparedSeedWalletSaved(seedDraft)} error={error} onCancel={() => {
                  setSeedDraft(null);
                  setError("");
                }} onConfirmed={() => handleCreateWallet(undefined, seedDraft)} pending={loading} words={seedDraft.mnemonic.split(" ")} /> : <form className="grid min-w-0 gap-4" onSubmit={handleCreateWallet}>
											<div className="grid gap-2">
												<Label htmlFor="displayName">Display name</Label>
												<Input autoComplete="name" disabled={loading} id="displayName" onChange={e_1 => setDisplayName(e_1.target.value)} placeholder="Satoshi" required value={displayName} />
											</div>

											<div className="grid gap-2">
												<Label htmlFor="password">Password</Label>
												<Input autoComplete="new-password" disabled={loading} id="password" minLength={MIN_PASSWORD_LENGTH} onChange={e_2 => setPassword(e_2.target.value)} placeholder="Create a strong password" required type="password" value={password} />
											</div>

											<div className="grid gap-2">
												<Label htmlFor="confirmPassword">
													Confirm password
												</Label>
												<Input autoComplete="new-password" disabled={loading} id="confirmPassword" minLength={MIN_PASSWORD_LENGTH} onChange={e_3 => setConfirmPassword(e_3.target.value)} placeholder="Confirm password" required type="password" value={confirmPassword} />
											</div>

											<SignupSubmitButton loading={loading} onRetry={handleRetryCloudBackup} pendingCloudBackup={Boolean(pendingCloudBackupBapId)} />
										</form>}

									<p className="text-center text-muted-foreground text-sm">
										Already have a Sigma identity?{" "}
										<Link className="underline underline-offset-4" href={loginHref} prefetch={true}>
											Sign in
										</Link>
									</p>
								</CardContent>
							</Card>
						</div>
					</div>
				</div>

				<AuthHeroPanel clientImageUrl={clientImageUrl} oauthClient={oauthClient} />
			</div>

			<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-t px-4 py-4 text-center text-muted-foreground text-sm">
				<span className="min-w-0 text-xs">
					<AuthLegalNotice />
				</span>
				<span aria-hidden className="hidden sm:inline">
					·
				</span>
				<span className="min-w-0">
					Already have a wallet?{" "}
					<Link className="underline underline-offset-4" href={loginHref} prefetch={true}>
						Sign in
					</Link>
				</span>
				<Label className="sr-only" htmlFor="signup-backup-import">
					Import from backup
				</Label>
				<input accept=".json,.bep" className="sr-only" id="signup-backup-import" onChange={handleImportBackupInput} type="file" />
			</div>

			<DecryptedTextLoadingOverlay open={showSyncDialog} steps={syncSteps} username="Creating wallet" />
		</div>;
}
export default function SignupPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SignupContent />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}